import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  INPUT_TYPE_LABEL,
  ITEMS_RECEIVED_LABEL,
  SPEC_SCALE,
  SPEC_SCALE_LABEL,
  STAGE_LABEL,
  type ItemReceivedKey,
  type Kit,
  type Stage,
  canOpenView,
  confirmsActions,
  groupToothItems,
  isStage,
  sortTeeth,
  asksForProof,
  mustChooseDestination,
  nextStage as nextOnRoute,
  requiresProof,
  worksThisBench,
  worksThisCase,
  TAKE_OVER_QUESTION,
  assignedToSentence,
  finalCheckClosedTo,
  isFinalCheckRefusal,
  takeKind,
  takeOverStageRefusal,
} from '@/domain';
import { useSession } from '@/application/usecases/useSession';
import { useCatalog } from '@/application/usecases/useIntake';
import { useJobOrder } from '@/application/usecases/useOrders';
import {
  flagDestinationsFor,
  needsExplicitDestination,
  useAddKitPhoto,
  useKitHistory,
  useKitPhotos,
  useKitWorkProofs,
  useMarkUrgent,
  useCompleteWork,
  useFlagKit,
  useKit,
  useKitNotes,
  usePauseWork,
  useStartWork,
} from '@/application/usecases/useKits';
import { formatDuration, formatElapsed, useElapsed, usePausedSeconds } from '@/application/timer/timerStore';
import { strings } from '@/shared/strings';
import { formatInstant } from '@/shared/format/instant';
import { Odontogram } from '@/ui/data/Odontogram';
import { SpanList } from '@/ui/data/SpanList';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { reportCommandFailure } from '@/features/shell/reportCommandFailure';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { useOfferUndo } from '@/features/shell/useOfferUndo';
import { PauseConfirm } from '@/features/shell/PauseConfirm';
import {
  CheckIcon,
  ChevronIcon,
  FlagIcon,
  GoBackIcon,
  PauseIcon,
  PlayIcon,
  ReworkIcon,
  ScanIcon,
  StaffIcon,
} from '@/ui/icons';
import { CaseHistory } from '@/features/archive/CaseHistory';
import { ClientLink } from '@/features/clients/ClientLink';
import { AssignDialog } from './AssignDialog';
import { StagePickerDialog } from './StagePickerDialog';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { NoticeSheet } from '@/ui/primitives/NoticeSheet';
import { WorkProofSheet } from './WorkProofSheet';
import { CaseNotes } from '@/ui/data/CaseNotes';
import { CaseInstructions } from '@/ui/data/CaseInstructions';
import { CaseSummary } from './CaseSummary';
import { TeethViewer } from './TeethViewer';
import { usePhoneLayout } from '@/ui/layout/usePhoneLayout';
import { VIEW_PATH } from '@/ui/layout/navItems';
import { haptic } from '@/ui/motion/haptics';
import { DURATION } from '@/ui/motion/motion';
import { useFolded } from '@/application/usecases/useFolded';
import { CasePhotos } from './CasePhotos';
import { LabNotesButton } from '@/features/notes/LabNotesButton';
import { LabNotesSheet } from '@/features/notes/LabNotesSheet';
import { SpecFieldRow } from './SpecFieldRow';
import { EntryProofs, WorkProofStrip } from './WorkProofStrip';
import { SkeletonCase } from '@/ui/feedback/Loading';
import styles from './JobScreen.module.css';

/**
 * The job view. Section 5.2, `jobView()`.
 *
 * Opening a kit starts the timer, because the scan is the claim and a
 * technician who has the tray in their hand is working. Finishing offers two
 * actions and no more: Mark done, which advances along the kit's own route
 * automatically, and Flag, which asks which department takes it and requires a
 * note.
 *
 * Asking for a destination on every completion would add a tap to every task
 * and invite mis-routing, which is item 4 of section 17.
 */
export function JobScreen() {
  const { kitId } = useParams<{ kitId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * A case is TAKEN by scanning the tray, never by tapping a row in a list.
   *
   * The scanner sets this when it hands over, and typing the code off the tray
   * card counts too, because the code is on the tray and having it means having
   * the tray in front of you. Opening the same case from a queue, the
   * production line or an order still shows everything; it just does not claim
   * it. That was the client's instruction and it matches section 5.2, which
   * says a technician does not claim work from a list.
   *
   * Router state rather than a query parameter, so it cannot be forged by
   * typing a URL.
   */
  const scanState = location.state as { scanned?: boolean; startHandled?: boolean } | null;
  const arrivedByScan = scanState?.scanned === true;
  /*
   * Whether this scan's start has already been decided. The router keeps its
   * state in the browser's history entry, which outlives the page: after a
   * Pause, a reload (an iPhone resuming the app, an update, a refresh) brought
   * `scanned` back and started the clock again. Marked on the entry once used.
   */
  const startHandled = scanState?.startHandled === true;
  const { data: session } = useSession();
  /*
   * The price list, for the item's category. The route is decided by it, and a
   * renamed item keeps its category though its new name is on no list the
   * domain was built with.
   */
  const { data: catalog } = useCatalog();
  const { data: kit, isPending, isError } = useKit(kitId);
  const { data: notes } = useKitNotes(kitId);
  /*
   * The order, only while a deletion waits on the case. The kit says THAT one is
   * waiting, so the banner draws at once; the order says why and who asked, and
   * a case with no deletion on it costs no second call.
   */
  const { data: pendingOrder } = useJobOrder(
    kit?.deletionStatus === 'pending' ? kit.jobOrderNo : undefined,
  );

  /**
   * This case's own clock, or null when it is not running on this device.
   *
   * Derived, not stored: since section 1 of the request of 20 September 2026 the
   * device keeps no count, and the clock is the wall time since the server's
   * `startedAt` plus whatever the open entry already held. The subscription is to
   * this one kit and to the shared tick, so a screen this size re-renders once a
   * second and not on anything it is not showing.
   */
  const runningElapsed = useElapsed(kitId);
  const pausedSeconds = usePausedSeconds(kitId);

  /*
   * A case's own history and log. Section 6 of the request of 20 September 2026:
   * everybody who could open a case could read its timeline, and the client
   * decided otherwise. Read before the queries, because it decides whether one of
   * them is asked at all.
   */
  const canSeeCaseLog = session?.permissions.includes('seeCaseLog') === true;

  const startWork = useStartWork();
  const pauseWork = usePauseWork();
  const completeWork = useCompleteWork();
  const flagKit = useFlagKit();
  // Undo, on the toast after an action. Section 6 of the request of 5 October 2026.
  const offerUndo = useOfferUndo();
  const ask = useConfirmAction();
  const addPhoto = useAddKitPhoto();
  const photos = useKitPhotos(kitId);
  // The proofs of finished work: the caller's own, or all of them for a holder
  // of seeWorkProof. The server decides which.
  const workProofs = useKitWorkProofs(kitId);
  // Only for a caller who may read it: the endpoint refuses without `seeCaseLog`,
  // and a query that always 403s would retry and log on every case opened.
  const history = useKitHistory(canSeeCaseLog ? kitId : undefined);
  const markUrgent = useMarkUrgent();

  /*
   * Whether THIS person may work THIS bench, the server's rule stated once in
   * the domain, so the screen does not offer a Start it knows will be refused
   * and does not refuse one the server would allow. It used to keep its own
   * copy, which had fallen behind the API on one door: reception, who may
   * create a case, may start it at Intake.
   */
  const worksBench = (stage: Stage): boolean =>
    session !== undefined &&
    session !== null &&
    worksThisBench(
      {
        role: session.role,
        department: session.user.department,
        isIntakeOperator: session.user.isIntakeOperator,
        permissions: session.permissions,
      },
      stage,
    );
  /*
   * Or a case given to this person by name, which may be at any bench: a case
   * may be assigned to anybody. Request of 5 October 2026.
   */
  const worksCase = (stage: Stage, assignedToUserId: string | null): boolean =>
    session !== undefined &&
    session !== null &&
    worksThisCase(worksBench(stage), assignedToUserId, session.user.id);
  /*
   * Opened from a notification about a case that has since moved on from this
   * person (marked done and sent to another bench or technician): ask first,
   * and if they want to see it, show it to read only. No scan, no clock, no
   * actions. Request of 5 October 2026. Management, who work every bench, are
   * never asked.
   */
  const fromNotification =
    (location.state as { fromNotification?: boolean } | null)?.fromNotification === true;
  const [movedOnAnswer, setMovedOnAnswer] = useState<'ask' | 'view'>('ask');
  const viewOnly =
    fromNotification &&
    kit !== undefined &&
    session?.role !== 'management' &&
    !worksCase(kit.stage, kit.assignedToUserId);
  const askingMovedOn = viewOnly && movedOnAnswer === 'ask';
  const canMarkUrgent = !viewOnly && session?.permissions.includes('markUrgent') === true;
  const canEditCase = !viewOnly && session?.permissions.includes('editCase') === true;
  const canAssign = !viewOnly && session?.permissions.includes('assign') === true;
  /*
   * Taking a case off another bench. Section 7 of the request of 20 September
   * 2026, behind a permission the lab can take back from one bench without a
   * deploy. `myBench` is where it would go: reception and accounts work none, and
   * management works them all and needs no transfer.
   */
  const canTakeOver = session?.permissions.includes('takeOver') === true;
  const myBench: Stage | null =
    session !== undefined && session !== null && isStage(session.user.department)
      ? session.user.department
      : null;

  const [dialog, setDialog] = useState<
    'flag' | 'route' | 'assign' | 'proof' | 'takeOver' | 'person' | 'start' | 'doneHere' | 'pause' | null
  >(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [teethOpen, setTeethOpen] = useState(false);
  // Where the small chart was when it was enlarged, for the large one to grow out of.
  const [teethFrom, setTeethFrom] = useState<DOMRect | null>(null);
  const chart = useRef<HTMLDivElement>(null);
  /*
   * The task bar is fixed above the tab bar on a phone, so the case needs room
   * under its last line for the bar's height, whatever it is folded or open.
   */
  const bar = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  useEffect(() => {
    const node = bar.current;
    if (node === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setBarHeight(node.offsetHeight));
    observer.observe(node);
    return () => observer.disconnect();
  });
  const autoStarted = useRef(false);

  /*
   * The message that a case at Final Check cannot be taken over, with its one
   * OK button. The request of 5 October 2026 (third), section 1.
   *
   * State, set from three places and nowhere else: a scan arriving, a press on
   * a button that would send the person to the scanner, and the server refusing
   * a start for this reason. Never worked out while drawing: the screen draws
   * again every second under a running clock, and a message derived from the
   * case would be back the moment OK was pressed.
   */
  const [finalCheckNotice, setFinalCheckNotice] = useState(false);
  // When this screen arrived, so a scan's message can wait out the page change.
  const arrivedAt = useRef(0);
  useEffect(() => {
    arrivedAt.current = Date.now();
  }, []);

  /**
   * Why the task did not start.
   *
   * The callers used to pass no `onError` at all. The mutation's own handler
   * rolls the optimistic timer back and says nothing, so a refusal -- a 403 from
   * a bench that is not yours, a 409 because another case is already running --
   * showed as the clock flickering on and then off. The technician saw a button
   * that did nothing and pressed it again.
   *
   * A queued command is not a failure and must not be reported as one: the work
   * is recorded and will send. `reportCommandFailure` already draws that line,
   * and the timer stays running underneath it, so there is nothing to undo here.
   *
   * One refusal is not reported as a failure either. The server would not let
   * this person take a case at Final Check, which the screen did not know when
   * it offered the start (the setting was switched off, or the case moved or
   * was given to somebody, since it was read). That is the same message a scan
   * shows, not an error toast.
   */
  const reportStartFailure = (error: Error): void => {
    if (isFinalCheckRefusal(error)) {
      setFinalCheckNotice(true);
      return;
    }
    reportCommandFailure(error, () => {});
  };

  /*
   * Lab Notes, open over the case. `?notes=1` opens it on arrival, which is how
   * a notification about a message lands on the conversation rather than on the
   * case. Request of 4 October 2026, section 7.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const notesOpen = searchParams.get('notes') === '1' && !askingMovedOn;
  /*
   * Opening pushes a history entry, so the phone's own Back closes Lab Notes
   * and lands on the case; the case stays mounted underneath, so it is where it
   * was scrolled to. Closing goes back through that entry when there is one,
   * and otherwise (arrived from a notification) just drops the parameter.
   */
  const pushedNotes = (location.state as { notesPushed?: boolean } | null)?.notesPushed === true;
  const setNotesOpen = (open: boolean) => {
    if (!open && pushedNotes) {
      void navigate(-1);
      return;
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (open) next.set('notes', '1');
        else next.delete('notes');
        return next;
      },
      {
        replace: !open,
        state: open
          ? { ...((location.state as Record<string, unknown> | null) ?? {}), notesPushed: true }
          : (location.state as unknown),
      },
    );
  };

  /*
   * The current task card folds to one row: the job order, the task, the clock
   * and Pause or Resume. Request of 4 October 2026, section 4, which replaced
   * the phone-only fold of the request of 24 September 2026 with one for every
   * width, and asked for it to be remembered.
   *
   * Until the person has folded or opened it, the card decides once the case
   * is known: open for whoever is working it, folded on a phone for somebody
   * only looking, as the earlier request had it. From the first tap on, the
   * card is as they last left it, on every case, on this device.
   */
  const phone = usePhoneLayout();
  const [foldChoice, setFoldChoice] = useFolded('current-task');

  /**
   * Scanning a kit starts the timer.
   *
   * It used to refuse when another task was already running, mirroring the
   * server's 409. Section 2 of the request of 20 September 2026 removed both: a
   * technician scanning a third tray is working a third case, and the clock on
   * each runs until that case is paused or done.
   *
   * Opening the same kit from a list does not start anything. Taking a case is
   * an act performed on the bench with the tray in hand, and the scan is the
   * evidence of it.
   */
  useEffect(() => {
    if (kit === undefined || autoStarted.current) return;
    if (!arrivedByScan || startHandled) return;
    // Decided once per scan, whatever is decided: this entry never starts again.
    void navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...scanState, startHandled: true },
    });
    /*
     * A manager's scan opens the case to LOOK at it. The client was explicit:
     * they walk the floor scanning trays to see where things are, and a clock
     * starting in their name every time would be noise in the productivity
     * figures and a kit claimed by somebody who is not going to work it. They
     * still get a Start button, because they may.
     *
     * A technician scanning a tray from another bench does not start either;
     * the screen says whose bench it is instead of sending a request that
     * will come back 403.
     */
    if (session?.role === 'management') return;
    /*
     * Nor does a scan take a case OVER: one at another department, or one
     * assigned to somebody else. Section 9 of the request of 5 October 2026.
     * The screen warns first, naming who holds it, and sends nothing until the
     * person confirms; the server would refuse an unconfirmed take anyway.
     */
    const scanned = takeKind({
      worksItsDepartment: worksBench(kit.stage),
      assignedToUserId: kit.assignedToUserId,
      userId: session?.user.id ?? '',
    });
    if (scanned === 'takeOver') {
      /*
       * At Final Check, while the lab's setting is off, there is nothing to
       * confirm either: the case cannot be taken over, and the scan says so in
       * a message with one OK button, whether the task card is folded or open.
       * The request of 5 October 2026 (third), section 1. Below the manager's
       * return above on purpose: their scan is a look, and they are told only
       * when they try to take the case. A held case is told too; the banner is
       * about the hold, and this is about the stage.
       *
       * Not before the page change has finished. The sheet is drawn inside the
       * screen that is sliding in, and one put up while that moves would be
       * placed against the moving screen and jump when it settled.
       */
      if (
        !finalCheckClosedTo({
          stage: kit.stage,
          kind: scanned,
          allowedAtFinalCheck: session?.settings.takeOverAtFinalCheck === true,
        })
      ) {
        return;
      }
      const settled = Math.max(0, DURATION.normal - (Date.now() - arrivedAt.current));
      const opening = window.setTimeout(() => setFinalCheckNotice(true), settled);
      return () => window.clearTimeout(opening);
    }
    // Nor on a held case: the banner says why, and the server would refuse it.
    if (kit.held) return;
    autoStarted.current = true;

    // Already running on this device is a no-op: the server returns the task it
    // already has, and starting it again would only move its `startedAt`.
    if (runningElapsed === null) {
      startWork.mutate(
        { kitId: kit.id, source: 'qr' },
        {
          onError: reportStartFailure,
          // Taking an unassigned case by scanning it can be undone.
          onSuccess: (result) => {
            if (result.kit.undo != null) {
              offerUndo(`${kit.jobOrderNo} ${strings.job.tookCase}`, result.kit.undo, 'neutral');
            }
          },
        },
      );
    }
    // Only the wait before the Final Check message has anything to clear.
    return undefined;
    // Runs once per kit, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kit?.id]);

  if (isPending) return <SkeletonCase label={strings.common.loadingKit} />;
  if (isError || kit === undefined) return <p>{strings.errors.server}</p>;

  /*
   * Running, or not. There is no third state any more.
   *
   * The screen used to distinguish "paused with time on the clock" from "never
   * started", so it could offer Resume and keep the count on screen. Since the
   * device holds no count, a paused task reads zero here and its time is on the
   * server's open entry: pressing Start again takes it up where it left off, which
   * is what Resume did, so one button says it.
   */
  const running = runningElapsed !== null;
  /*
   * Paused, the clock stays at the time it reached: this device's own reading
   * from the moment of pausing, or the open entry's worked time where the case
   * log is readable. Zero only for a case nobody has started. 5 October 2026.
   */
  const openEntrySeconds = history.data?.find(
    (entry) => entry.endedAt === null && entry.stage === kit.stage,
  )?.activeSeconds;
  const elapsed = runningElapsed ?? pausedSeconds ?? openEntrySeconds ?? 0;

  const taskFolded = foldChoice ?? (phone && !(running || arrivedByScan));


  /*
   * The items on the case, one entry each.
   *
   * A case with an empty chart -- taken over the phone on a unit count, teeth
   * to follow -- falls back to the case's own item so this is never empty and
   * the panel below never has to handle a missing row.
   */
  const itemGroups = groupToothItems(kit.toothItems);
  const items = itemGroups.length > 0 ? itemGroups : [{ product: kit.product, teeth: [] }];

  // The server's count, refetched after an upload. Adding a local optimistic
  // count on top of it counts the same photo twice.
  const photosAttached = kit.photoCount;

  /*
   * The routing mode, from the session. In manual mode every Mark done asks
   * where the case goes next; in automatic mode only a case off its own route
   * does. Section 2.3 of the system test handover.
   */
  const routing = session?.settings.routing ?? 'automatic';
  // Proof of work, from the session for the same reason. Section 2 of the
  // request of 13 September 2026.
  const workProof = session?.settings.workProof ?? 'off';

  /*
   * Mark done, in order: the proof of work first, then the route, then the
   * completion. Where the case goes is the last decision. The photograph is
   * taken before the completion rather than with it, so a failed upload leaves
   * the case where it was.
   */
  function finish() {
    /*
     * No photograph is demanded here. Ceramic used to lock Mark done until one
     * was attached, and a case with nothing to photograph could not be
     * finished. Whether a photo is needed is the lab's Proof of work setting,
     * asked for below; the panel above still takes one when there is one.
     */
    if (asksForProof(workProof)) {
      setDialog('proof');
      return;
    }
    finishAfterProof();
  }

  function finishAfterProof() {
    if (mustChooseDestination(routing, needsExplicitDestination(kit as Kit, itemCategory))) {
      setDialog('route');
      return;
    }
    /*
     * On its route, the destination is decided, and the sheet asks only who
     * takes it and whether there is a note for Lab Notes, then confirms. Request
     * of 4 October 2026, sections 5 and 6. A case at the end of its route has
     * nowhere to choose and completes at once.
     */
    if (nextOnRoute((kit as Kit).stage, (kit as Kit).input, (kit as Kit).product, itemCategory) !== null) {
      setDialog('person');
      return;
    }
    setDialog('doneHere');
  }

  function completeHere() {
    completeWork.mutate(
      { kit: kit as Kit, nextStage: null },
      {
        onSuccess: (result) => {
          // The SERVER's figure, off the entry it just closed, not this device's.
          // Section 1.4: there is only one source now, and this is where the
          // technician sees what it recorded.
          haptic('success');
          offerUndo(
            `${result.kit.jobOrderNo} ${strings.job.completed} · ${formatDuration(result.historyEntry.activeSeconds)} ${strings.job.logged}`,
            result.kit.undo,
          );
          setDialog(null);
          leaveCase();
        },
        onError: (error: Error) => {
          setDialog(null);
          reportCommandFailure(error, leaveCase);
        },
      },
    );
  }

  /*
   * A case is opened from three places now, so leaving it cannot always mean
   * "go to the queue". Management holds no `queue` view, and sending them there
   * from a case they opened on the production line lands them back on their
   * home screen having lost their place.
   */
  const hasQueue = session?.views.includes('queue') === true;
  const backLabel = hasQueue ? strings.job.back : strings.job.backGeneric;
  const leaveCase = () => {
    // A technician goes back to their queue. Anybody else came from a list they
    // hold, so history is the only honest destination...
    if (hasQueue) void navigate('/queue');
    // ...unless they came from the scanner: going back reopened the camera over
    // the confirmation of what they had just done. Their home screen instead.
    else if (arrivedByScan && session !== undefined && session !== null) {
      void navigate(VIEW_PATH[session.homeView], { replace: true });
    } else void navigate(-1);
  };


  /*
   * Why the case is here, when it did not arrive by the ordinary road.
   *
   * Final Check writes its reason onto the history entry it closes, and so
   * does a flag. The kit itself only carries a `rework` bit, which is why a
   * technician could see that a case had come back and not what was wrong
   * with it. The latest closed entry with a note is the one that sent it.
   */
  const itemCategory = catalog?.items.find((item) => item.id === kit.catalogItemId)?.category;
  const offRoute = needsExplicitDestination(kit, itemCategory);
  // The station the automatic route would pick, preselected in manual mode.
  // Null for a case off its route: there is nothing to suggest.
  const suggested = offRoute ? null : nextOnRoute(kit.stage, kit.input, kit.product, itemCategory);
  const routeDestinations =
    suggested === null || flagDestinationsFor(kit).includes(suggested)
      ? flagDestinationsFor(kit)
      : [...flagDestinationsFor(kit), suggested];
  const entries = history.data ?? [];
  /*
   * Why the case is here, when it did not arrive by the ordinary road.
   *
   * Read off the NOTES read model, not the history entry. Section 6 of the
   * request of 20 September 2026 puts the case timeline behind `seeCaseLog`, and
   * this banner is the reason a technician needs: it must not go behind a
   * manager's permission. The notes read model carries the rework and flag
   * reasons with their `fromStage` and `toStage` and is gated on nothing but
   * opening the case, so the banner reads the same fact from a source everybody
   * has. Section 3 of the request of 16 September 2026 built it.
   */
  const sentBack = offRoute
    ? ((notes ?? []).find(
        // One that was taken back sent nothing back.
        (note) => (note.kind === 'rework' || note.kind === 'flag') && note.undoneAt == null,
      ) ?? null)
    : null;
  /*
   * What taking this case would be, for this person. Section 9 of the request
   * of 5 October 2026: already theirs, simply taken (nobody holds it, at their
   * own department), or a take-over, which is a case somebody else holds or one
   * at another department. Only the last has rules, and they are the server's;
   * the screen reads the same ones so it can say why before anything is pressed.
   */
  const worksItsDepartment = worksBench(kit.stage);
  const take = takeKind({
    worksItsDepartment,
    assignedToUserId: kit.assignedToUserId,
    userId: session?.user.id ?? '',
  });
  const mayWork = take !== 'takeOver';
  // Ready and Logistics never; Final Check only while management allows it.
  const takeOverRefusal =
    take === 'takeOver'
      ? takeOverStageRefusal(kit.stage, session?.settings.takeOverAtFinalCheck === true)
      : null;
  // Who holds it, and who gave it to them: the warning.
  const holderSentence =
    take === 'takeOver' && kit.assignedToUserId !== null && kit.assignedToUserName !== null
      ? assignedToSentence(kit.assignedToUserName, kit.assignedByUserName ?? null)
      : null;
  // A case at their own department changes hands; one at another needs the
  // permission and a department of their own to bring it to.
  const mayTakeOver =
    take === 'takeOver' &&
    takeOverRefusal === null &&
    !kit.held &&
    (worksItsDepartment || (canTakeOver && myBench !== null));

  /*
   * Pause, or Resume for the person who may take it up again: the one control
   * the folded card keeps. Resume is Start, which takes the open entry up
   * where it left off.
   */
  const mayResume = !running && arrivedByScan && mayWork && !kit.held;

  /*
   * "Scan the tray to take this case", on a case opened from a list. For
   * somebody Final Check is closed to, the scanner would only bring them back
   * here to be told so, so the button tells them now instead of opening the
   * camera. The request of 5 October 2026 (third), section 1. Everybody else
   * goes to the scanner as before. A manager works every department, so for
   * them it is closed only when the case is assigned to somebody else.
   */
  const scanToTake = () => {
    if (
      finalCheckClosedTo({
        stage: kit.stage,
        kind: take,
        allowedAtFinalCheck: session?.settings.takeOverAtFinalCheck === true,
      })
    ) {
      setFinalCheckNotice(true);
      return;
    }
    void navigate('/scan');
  };

  // Where the whole case history is, for a caller whose screens include the record.
  const views = session?.views ?? [];
  const recordPath = views.includes('orders')
    ? `/orders/${kit.jobOrderNo}/record`
    : canOpenView(views, 'record')
      ? `/archive/${kit.jobOrderNo}`
      : undefined;

  return (
    <div style={phone && barHeight > 0 ? { paddingBottom: barHeight + 12 } : undefined}>
      <button className={styles.back} onClick={leaveCase}>
        <GoBackIcon /> {backLabel}
      </button>

      {/*
        The top of the case. Request of 4 October 2026 (second), section 8. It
        scrolls with the case rather than staying pinned, as asked on 5 October.
      */}
      <CaseSummary kit={kit} running={running} />
      {viewOnly && !askingMovedOn && (
        <p className={styles.readOnly} role="status">
          {strings.movedOn.readOnly}
        </p>
      )}

      <div className={styles.job}>
        {/*
          An archived case, opened from a link or a notification. It is off
          every bench's list, and every command that works it is refused in
          these words, so the bench reads them before it presses anything.
          Section 4 of the request of 24 September 2026.
        */}
        {kit.archived && (
          <div className={`${styles.notes} ${styles.banner}`} role="status">
            <div className={styles.h}>{strings.archived.banner}</div>
            <div className={styles.note}>{strings.archived.notice}</div>
          </div>
        )}

        {/*
          A case on hold. The lab has stopped it, so the work controls are not
          drawn, rather than drawn and refused: a missing Mark done says the
          case is not available, a disabled one invites a tap and a 409. Section
          9 of the review of 2 October 2026.
        */}
        {kit.held && (
          <div className={`${styles.notes} ${styles.banner}`} role="status">
            <div className={styles.h}>{strings.job.heldBanner}</div>
            <div className={styles.note}>{kit.holdReason ?? strings.job.heldNotice}</div>
            {kit.holdReason !== null && <div className={styles.m}>{strings.job.heldNotice}</div>}
          </div>
        )}

        {/*
          A deletion waiting for approval. The case is not locked: a request is
          one person's opinion until somebody else agrees, so the work controls
          stay and the bench knows.
        */}
        {kit.deletionStatus === 'pending' && (
          <div className={`${styles.notes} ${styles.banner}`} role="status">
            <div className={styles.h}>{strings.deletion.waitingBanner}</div>
            {pendingOrder?.jobOrder.deletion.reason != null && (
              <div className={styles.note}>
                <div>{pendingOrder.jobOrder.deletion.reason}</div>
                <div className={styles.m}>
                  {[
                    pendingOrder.jobOrder.deletion.requestedByUserName,
                    pendingOrder.jobOrder.deletion.requestedAt === null
                      ? null
                      : formatInstant(pendingOrder.jobOrder.deletion.requestedAt),
                  ]
                    .filter((part) => part !== null)
                    .join(' · ')}
                </div>
              </div>
            )}
          </div>
        )}

        {sentBack !== null && (
          <div className={`${styles.notes} ${styles.banner}`} role="status">
            <div className={styles.h}>
              <ReworkIcon />{' '}
              {kit.rework
                ? strings.job.sentBack
                : `${strings.job.sentHere} ${
                    sentBack.fromStage === null ? '' : STAGE_LABEL[sentBack.fromStage]
                  }`}
            </div>
            <div className={styles.note}>
              <div>{sentBack.text}</div>
              <div className={styles.m}>
                {sentBack.byUserName} · {formatInstant(sentBack.createdAt)}
              </div>
            </div>
          </div>
        )}

        {/*
          What the dentist wants and what the lab wants, in full, directly under
          the head. Section 9 of the request of 20 September 2026 put them here:
          the dentist's instructions used to sit after the case reference, the
          specification and the items received, which is most of a screen below
          the head on a phone, and the bench scrolled past them.

          In full, with nothing to press. The request of 24 September 2026 took
          away the Doctor, Lab and Notes toggles that stood here: on a phone they
          read as buttons that did nothing, because the panels were already open,
          and a panel folded by accident stayed folded on that device. The block
          under Shade & finish that repeated the doctor's words went with them,
          so they are on the screen once.
        */}
        {/*
          The case in one grid, labels over values: who it is for, what it is
          and the shade. A long dentist or patient name wraps under its label
          rather than being cut, as on the tray sheet. Request of 4 October 2026
          (second), section 8.

          Each of the two names opens its profile for a reader who holds that
          screen, a manager looking at a case, and stays the plain name for
          anybody else, the bench included. The request of 6 October 2026
          (second), item 4.
        */}
        <section className={styles.details} aria-labelledby="case-details">
          <h3 id="case-details">{strings.caseView.details}</h3>
          <dl className={styles.detailGrid}>
            {kit.dentistName !== null && (
              <div className={styles.wide}>
                <dt>{strings.caseView.doctor}</dt>
                <dd dir="auto">
                  <ClientLink kind="doctor" id={kit.doctorId} name={kit.dentistName} />
                </dd>
              </div>
            )}
            {kit.patientName !== null && (
              <div className={styles.wide}>
                <dt>{strings.caseView.patient}</dt>
                <dd dir="auto">
                  <ClientLink kind="patient" id={kit.patientId} name={kit.patientName} />
                </dd>
              </div>
            )}
            <div>
              <dt>{strings.caseView.caseType}</dt>
              <dd>{INPUT_TYPE_LABEL[kit.input]}</dd>
            </div>
            <div>
              <dt>{strings.caseView.material}</dt>
              <dd>{items.map((group) => group.product).join(', ')}</dd>
            </div>
            <div>
              <dt>{strings.caseView.shade}</dt>
              <dd className={styles.mono}>{kit.shade}</dd>
            </div>
            <div>
              <dt>{strings.caseView.units}</dt>
              <dd>
                {kit.units}
                {kit.teeth.length > 0 && ` · ${kit.teeth.join(', ')}`}
              </dd>
            </div>
            <div>
              <dt>{strings.caseView.received}</dt>
              <dd className={styles.mono}>{kit.startDate}</dd>
            </div>
          </dl>
        </section>

        <CaseInstructions text={kit.instructions} flush />

        {/*
          Every note about the case, gathered by meaning rather than by where it
          was stored, and a box to add one. Section 3 of the request of
          16 September 2026. The Sent back from Final Check banner above is
          untouched: it reads the history entry, which is what confirmation 1 of
          the system test handover depends on, and this reads the same entry
          without replacing it.

          The newest three, and the rest behind a button that says how many, so
          a case with a long conversation on it does not push its own details
          off a phone. The one place notes are shown and written, since the
          request of 24 September 2026.
        */}
        <section className={styles.notes} aria-label={strings.labNotes.title}>
          <div className={styles.notesHead}>
            <div className={styles.h}>{strings.labNotes.title}</div>
            <LabNotesButton kitId={kit.id} onOpen={() => setNotesOpen(true)} />
          </div>
          <CaseNotes
            notes={notes ?? []}
            limit={3}
            // A proof row shows its photographs, to whoever was given them. The
            // request of 5 October 2026 (third), section 6.
            extra={(note) =>
              note.kind === 'proof' && (
                <EntryProofs
                  proofs={workProofs.data}
                  historyEntryId={note.historyEntryId}
                  jobOrderNo={kit.jobOrderNo}
                />
              )
            }
          />
        </section>

        <div className={styles.columns}>
          <div className={`${styles.col} ${styles.left}`}>
            <section className={styles.blk}>
              <h4>{strings.job.caseReference}</h4>
              <div className={styles.row}>
                <div className={styles.field}>
                  <div className={styles.l}>{strings.job.jobOrder}</div>
                  <div className={`${styles.v} ${styles.mono}`}>{kit.jobOrderNo}</div>
                </div>
                <div className={styles.field}>
                  <div className={styles.l}>{strings.job.kitId}</div>
                  <div className={`${styles.v} ${styles.mono}`}>{kit.id}</div>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.field}>
                  <div className={styles.l}>{strings.job.input}</div>
                  <div className={styles.v}>{INPUT_TYPE_LABEL[kit.input]}</div>
                </div>
                {/*
                  Every item on the case, not just its headline one.

                  A mixed case names the teeth against each item. Showing only
                  kit.product here would tell a technician the tray is six
                  Zircon crowns when two of them are E.max veneers, and this
                  panel is what they read before picking up a bur.
                */}
                {items.map((group) => (
                  <div key={group.product} className={styles.field}>
                    <div className={styles.l}>
                      {items.length > 1 ? group.teeth.join(', ') : strings.job.restoration}
                    </div>
                    <div className={styles.v}>{group.product}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className={styles.blk}>
              <h4>{strings.job.specification}</h4>
              <div className={styles.row}>
                <OptionRow
                  label={strings.job.translucency}
                  options={SPEC_SCALE.map((step) => SPEC_SCALE_LABEL[step])}
                  selected={SPEC_SCALE_LABEL[kit.specification.translucency]}
                />
                <OptionRow
                  label={strings.job.anatomy}
                  options={SPEC_SCALE.map((step) => SPEC_SCALE_LABEL[step])}
                  selected={SPEC_SCALE_LABEL[kit.specification.anatomy]}
                />
              </div>
              {/*
                Incisal transparency and occlusal stain are NOT repeated here.
                They live under Shade and finish, where a ceramist reads them
                alongside the shade, and a value shown twice on one screen is a
                value somebody will eventually see disagree with itself.
              */}
              {/*
                The two fields the BENCH knows. Wax chose the ingot and CAD / CAM
                milled the block, and reception was guessing at both; each is
                editable in place by the department that owns it, and by a holder
                of editCase. Section 1 of the request of 13 September 2026.
              */}
              <div className={styles.row}>
                <SpecFieldRow kit={kit} field="ingot" />
                <SpecFieldRow kit={kit} field="block" />
              </div>
              <div className={styles.row}>
                <div className={styles.field}>
                  <div className={styles.l}>{strings.job.stain}</div>
                  <div className={styles.v}>{kit.specification.stain}</div>
                </div>
              </div>
            </section>

            <section className={styles.blk}>
              <h4>{strings.job.itemsIncluded}</h4>
              <div className={styles.items}>
                {(Object.keys(ITEMS_RECEIVED_LABEL) as ItemReceivedKey[]).map((key) => (
                  <span
                    key={key}
                    className={`${styles.item} ${kit.itemsReceived[key] ? styles.on : ''}`}
                  >
                    <span className={styles.box}>{kit.itemsReceived[key] && <CheckIcon />}</span>
                    {ITEMS_RECEIVED_LABEL[key]}
                  </span>
                ))}
              </div>
            </section>
          </div>

          <div className={styles.col}>
            <section className={`${styles.blk} ${styles.chartBlk}`}>
              <div className={styles.chartHead}>
                <h4>{strings.job.teeth}</h4>
                <button
                  type="button"
                  className={styles.enlarge}
                  aria-label={strings.caseView.enlargeLabel}
                  onClick={() => {
                    setTeethFrom(chart.current?.getBoundingClientRect() ?? null);
                    setTeethOpen(true);
                  }}
                >
                  {strings.caseView.enlarge}
                </button>
              </div>
              {/*
                The same chart used to create the order, so a technician who
                scans a tray sees the mouth rather than a list of numbers to
                decode. Read only here: the case is already specified.
              */}
              <div ref={chart}>
              <Odontogram
                selected={sortTeeth(kit.teeth)}
                readOnly
                size="compact"
                spans={kit.spans}
                showSelection
              />
              </div>
              <div className={styles.teethList}>
                {kit.teeth.length === 0
                  ? `${kit.units} ${kit.units === 1 ? strings.job.toothUnit : strings.job.toothUnits} · ${strings.job.chartNotMarked}`
                  : kit.teeth.join(', ')}
              </div>
              {/* The bridge the sheet shows, so a technician at a tray sees it too. */}
              <SpanList spans={kit.spans} heading={strings.job.bridges} />
            </section>

            <section className={styles.blk}>
              <h4>{strings.job.shadeFinish}</h4>
              <div className={styles.row}>
                <div className={styles.field}>
                  <div className={styles.l}>{strings.job.shade}</div>
                  <div className={`${styles.v} ${styles.mono}`}>{kit.shade}</div>
                </div>
                <OptionRow
                  label={strings.job.incisal}
                  options={SPEC_SCALE.map((step) => SPEC_SCALE_LABEL[step])}
                  selected={SPEC_SCALE_LABEL[kit.specification.incisal]}
                />
              </div>
              <OptionRow
                label={strings.job.occlusal}
                options={SPEC_SCALE.map((step) => SPEC_SCALE_LABEL[step])}
                selected={SPEC_SCALE_LABEL[kit.specification.occlusal]}
              />
            </section>

            {/*
              The whole story of the case, on the case: the same card the
              record carries, cut to the newest five rows.

              ABSENT without `seeCaseLog`, not hidden. Section 6 of the request of
              20 September 2026 put the case timeline behind a permission; the
              endpoints behind this card refuse without it, so drawing an empty
              card would be drawing a card about a refusal.
            */}
            {canSeeCaseLog && (
              <section className={styles.blk}>
                <CaseHistory
                  jobOrderNo={kit.jobOrderNo}
                  stages={entries}
                  limit={5}
                  wholeHistoryTo={recordPath}
                  currentStages={[kit.stage]}
                />
              </section>
            )}
          </div>
        </div>
      </div>

      {/*
        The case photographs, shown to whoever opens the case.

        Reception attaches these at intake: the impression as it arrived, and
        the patient's mouth. A technician who has just scanned the tray is
        exactly the person they were taken for, which is why they sit above the
        bench controls rather than behind a tab.

        Intake photographs are separated from the bench's own, because they
        mean different things. One is what the case looked like when it came
        in; the other is a record of work done.
      */}
      {(photos.data?.length ?? 0) > 0 && (
        <section className={styles.photos}>
          <h4>{strings.job.photosHeading}</h4>
          <CasePhotos photos={photos.data ?? []} jobOrderNo={kit.jobOrderNo} />
        </section>
      )}

      {/*
        The proofs of finished work, beside the case photographs and never mixed
        in with them: what the last stage handed over, for the bench that takes
        it next. The caller's own, or everybody's for a holder of seeWorkProof.
      */}
      {(workProofs.data?.length ?? 0) > 0 && (
        <section className={styles.photos}>
          <h4>{strings.proof.cardTitle}</h4>
          <WorkProofStrip proofs={workProofs.data ?? []} jobOrderNo={kit.jobOrderNo} />
        </section>
      )}

      {kit.stage === 'ceramic' && (
        <div className={styles.gate}>
          <h4>{strings.job.photoGateHeading}</h4>
          <div className={`${styles.gateState} ${photosAttached > 0 ? styles.met : ''}`}>
            {photosAttached > 0
              ? `${photosAttached} ${photosAttached === 1 ? strings.job.photoGateMet : strings.job.photoGateMetPlural}`
              : strings.job.photoGateOpen}
          </div>
          <div className={styles.upload}>
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              aria-label={strings.job.photoInputLabel}
              className={styles.fileInput}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file === undefined) return;
                addPhoto.mutate({ kitId: kit.id, file });
                event.target.value = '';
              }}
            />
            <Button onClick={() => fileInput.current?.click()} disabled={addPhoto.isPending}>
              {photosAttached > 0 ? strings.job.attachAnother : strings.job.attachPhoto}
            </Button>
          </div>
        </div>
      )}

      <div
        ref={bar}
        // Read only: nothing to start, take or finish on a case that has moved on.
        hidden={viewOnly}
        className={`${styles.action} ${taskFolded ? styles.folded : ''}`}
        data-testid="task-bar"
      >
        {taskFolded ? (
          // One compact row. Request of 4 October 2026, section 4.
          <div className={styles.compact}>
            {running && <span className={styles.live} />}
            <span className={styles.compactWhat}>
              <b>{kit.jobOrderNo}</b>
              <span className={styles.compactTask}>{STAGE_LABEL[kit.stage]}</span>
            </span>
            <span className={`${styles.compactClock} ${running ? styles.run : ''}`} aria-live="off">
              {formatElapsed(elapsed)}
            </span>
            {running && (
              <button
                type="button"
                className={styles.compactButton}
                aria-label={strings.job.pause}
                onClick={() => setDialog('pause')}
              >
                <PauseIcon size={20} />
              </button>
            )}
            {mayResume && (
              <button
                type="button"
                className={`${styles.compactButton} ${styles.resume}`}
                aria-label={strings.job.resume}
                onClick={() => {
                  /*
                   * A desk role is asked first, in the Start sheet the unfolded
                   * card already opens; a technician resumes on the tap, as
                   * before. The request of 5 October 2026 (third), section 2.
                   */
                  if (confirmsActions(session?.role)) setDialog('start');
                  else startWork.mutate({ kitId: kit.id, source: 'qr' }, { onError: reportStartFailure });
                }}
              >
                <PlayIcon size={20} />
              </button>
            )}
            {/* Not taken yet: the way to take it, in the row. */}
            {!running && !arrivedByScan && !kit.held && (
              <button
                type="button"
                className={`${styles.compactButton} ${styles.resume}`}
                aria-label={strings.caseView.scanToTake}
                onClick={scanToTake}
              >
                <ScanIcon size={20} />
              </button>
            )}
            {arrivedByScan && !running && mayTakeOver && (
              <button
                type="button"
                className={`${styles.compactButton} ${styles.resume}`}
                aria-label={strings.job.takeOver}
                onClick={() => setDialog('takeOver')}
              >
                <PlayIcon size={20} />
              </button>
            )}
            <button
              type="button"
              className={styles.foldButton}
              aria-expanded={false}
              aria-label={strings.job.showTask}
              onClick={() => setFoldChoice(false)}
            >
              <ChevronIcon size={20} className={`${styles.chev} ${styles.chevUp}`} />
            </button>
          </div>
        ) : (
        <>
        <div className={styles.stage}>
          {strings.job.currentTask}
          <b>{STAGE_LABEL[kit.stage]}</b>
        </div>

        {/* Folds the card to one row, on every width. Request of 4 October 2026, section 4. */}
        <button
          type="button"
          className={styles.foldButton}
          aria-expanded={true}
          aria-label={strings.job.hideTask}
          onClick={() => setFoldChoice(true)}
        >
          <ChevronIcon size={20} className={styles.chev} />
        </button>

        <div className={styles.timer}>
          {running && <span className={styles.live} />}
          <div className={`${styles.clock} ${running ? styles.run : ''}`} aria-live="off">
            {formatElapsed(elapsed)}
          </div>
          {/* Nothing under a running clock: the line that said it runs until Pause
              went with the request of 4 October 2026, section 4. */}
          {!running && (
            <div className={styles.timerNote}>
              {arrivedByScan ? strings.job.fromScan : strings.job.notYours}
            </div>
          )}
        </div>

        {(
          <div className={styles.actions}>
            {!kit.held && (
              <Button icon={<FlagIcon />} onClick={() => setDialog('flag')}>
                {strings.job.flag}
              </Button>
            )}

            {/* Beside Flag, its pair: Flag moves the case, Assign gives it to a person. */}
            {canAssign && (
              <Button icon={<StaffIcon />} onClick={() => setDialog('assign')}>
                {strings.assign.action}
              </Button>
            )}

            {canMarkUrgent && (
              <Button
                disabled={markUrgent.isPending}
                // Asked about first: the request of 5 October 2026 (third), section 2.
                onClick={() =>
                  ask(
                    strings.confirm.urgent(kit.jobOrderNo, !kit.urgent),
                    () =>
                      markUrgent
                        .mutateAsync({ kitId: kit.id, urgent: !kit.urgent })
                        .then(() =>
                          toast(
                            `${kit.jobOrderNo} ${kit.urgent ? strings.job.urgentCleared : strings.job.urgentMarked}`,
                          ),
                        ),
                    {
                      title: kit.urgent ? strings.job.urgentOff : strings.job.urgentOn,
                      confirmLabel: kit.urgent ? strings.job.urgentOff : strings.job.urgentOn,
                    },
                  )
                }
              >
                {kit.urgent ? strings.job.urgentOff : strings.job.urgentOn}
              </Button>
            )}

            {canEditCase && (
              <Button onClick={() => void navigate(`/orders/${kit.jobOrderNo}/edit`)}>
                {strings.job.editCase}
              </Button>
            )}

            {/*
              Scanned, but standing at the wrong station.

              Without `takeOver` this is exactly what it was: the notice, and
              nothing sent. With it, section 7 of the request of 20 September 2026
              adds one button, and the confirmation says what will happen before
              anything moves.
            */}
            {arrivedByScan && !mayWork && !running && (
              <span className={styles.notice} role="status" data-testid="take-over-notice">
                {/*
                  Why it cannot be taken, when its stage says so: Ready and
                  Logistics never, Final Check while the setting is off. Otherwise
                  whose it is, or whose department it is at. For Final Check this
                  is the message the scan showed, both lines, left on the card
                  after OK. The request of 5 October 2026 (third), section 1.
                */}
                {takeOverRefusal !== null ? (
                  takeOverRefusal
                ) : worksItsDepartment && holderSentence !== null ? (
                  holderSentence
                ) : (
                  <>
                    {strings.job.notYourBench} <b>{STAGE_LABEL[kit.stage]}</b>,{' '}
                    {strings.job.notYourBenchTail}
                  </>
                )}
              </span>
            )}

            {arrivedByScan && !running && mayTakeOver && (
              <Button variant="primary" icon={<PlayIcon />} onClick={() => setDialog('takeOver')}>
                {strings.job.takeOver}
              </Button>
            )}

            {/*
              No Start button without a scan. Offering one would put the claim
              back on the list, which is the thing the client asked to remove.
            */}
            {!running && !arrivedByScan && !kit.held && (
              <Button variant="primary" icon={<ScanIcon />} onClick={scanToTake}>
                {strings.job.scanToTake}
              </Button>
            )}

            {!running && arrivedByScan && mayWork && !kit.held && (
              <Button
                variant="primary"
                icon={<PlayIcon />}
                onClick={() => setDialog('start')}
              >
                {strings.job.start}
              </Button>
            )}

            {running && (
              <div className={styles.pauseGroup}>
                <Button icon={<PauseIcon />} onClick={() => setDialog('pause')}>
                  {strings.job.pause}
                </Button>
              </div>
            )}

            {running && !kit.held && (
              <Button
                variant="primary"
                icon={<CheckIcon />}
                onClick={finish}
                disabled={completeWork.isPending}
              >
                {strings.job.markDone}
              </Button>
            )}

          </div>
        )}
        </>
        )}
      </div>

      {dialog === 'assign' && <AssignDialog kit={kit} onClose={() => setDialog(null)} />}

      {teethOpen && <TeethViewer kit={kit} from={teethFrom} onClose={() => setTeethOpen(false)} />}

      {/* A page over the case on a phone, a sheet elsewhere. Request of 4 October 2026 (second), section 4. */}
      {notesOpen && <LabNotesSheet kit={kit} page={phone} onClose={() => setNotesOpen(false)} />}

      {/*
        Taking the case over, with the warning the client asked for: who holds
        it and who gave it to them, and the question, before anything moves.
        Section 7.5 of the request of 20 September 2026; section 9 of the request
        of 5 October 2026. A case nobody holds, at another department, says
        where it will go instead. Confirming sends `takeOver`, which is what the
        server requires before it will reassign a case; Undo is on the toast.
      */}
      {dialog === 'takeOver' && mayTakeOver && (
        <ConfirmSheet
          title={strings.job.takeOver}
          statement={
            holderSentence !== null
              ? `${holderSentence} ${TAKE_OVER_QUESTION}`
              : strings.confirm.take(
                  kit.jobOrderNo,
                  STAGE_LABEL[kit.stage],
                  myBench === null ? STAGE_LABEL[kit.stage] : STAGE_LABEL[myBench],
                )
          }
          confirmLabel={strings.job.takeOverConfirm}
          cancelLabel={strings.job.takeOverCancel}
          closeLabel={strings.sheet.close}
          busy={startWork.isPending}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            startWork.mutate(
              { kitId: kit.id, source: 'qr', takeOver: true },
              {
                onSuccess: (result) => {
                  setDialog(null);
                  offerUndo(
                    `${kit.jobOrderNo} ${worksItsDepartment ? strings.job.takenOver : strings.job.taken}`,
                    result.kit.undo,
                    'neutral',
                  );
                },
                onError: (error: Error) => {
                  setDialog(null);
                  // The server's own words: it names the person whose clock
                  // is running, or says the case is on hold.
                  reportStartFailure(error);
                },
              },
            )
          }
        >
          {/* What will happen, who is told, and what the case is. */}
          <p className={styles.takeOverWhat}>
            {worksItsDepartment || myBench === null
              ? strings.job.takeOverStays
              : strings.job.takeOverMoves(STAGE_LABEL[myBench])}
          </p>
          {holderSentence !== null && (
            <p className={styles.takeOverWhat}>{strings.job.takeOverTold}</p>
          )}
          <p className={styles.takeOverWho}>
            {kit.jobOrderNo} · {kit.product} · {kit.units}{' '}
            {kit.units === 1 ? strings.job.toothUnit : strings.job.toothUnits}
          </p>
        </ConfirmSheet>
      )}

      {/* Taking the case on the clock, from the Start button. */}
      {dialog === 'start' && (
        <ConfirmSheet
          title={strings.job.start}
          statement={strings.confirm.start(kit.jobOrderNo, STAGE_LABEL[kit.stage])}
          confirmLabel={strings.job.start}
          cancelLabel={strings.confirm.cancel}
          closeLabel={strings.sheet.close}
          busy={startWork.isPending}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            startWork.mutate(
              { kitId: kit.id, source: 'qr' },
              {
                onSuccess: (result) => {
                  setDialog(null);
                  // A case nobody held is taken by starting it, which can be undone.
                  offerUndo(`${kit.jobOrderNo} ${strings.confirm.started}`, result.kit.undo, 'neutral');
                },
                onError: (error: Error) => {
                  setDialog(null);
                  reportStartFailure(error);
                },
              },
            )
          }
        />
      )}

      {/*
        A case at Final Check that cannot be taken over: the client's two lines
        and one OK button, not an error. The request of 5 October 2026 (third),
        section 1. OK leaves the person on the case; after a scan the same words
        stay on the open task card.
      */}
      {finalCheckNotice && (
        <NoticeSheet
          title={strings.job.finalCheckClosed.title}
          message={strings.job.finalCheckClosed.ask}
          okLabel={strings.sheet.ok}
          onClose={() => setFinalCheckNotice(false)}
        />
      )}

      {/* Mark done where the route ends and there is nowhere to choose. */}
      {dialog === 'doneHere' && (
        <ConfirmSheet
          title={strings.job.markDone}
          statement={strings.confirm.done(kit.jobOrderNo, null, null)}
          confirmLabel={strings.job.markDone}
          cancelLabel={strings.confirm.cancel}
          closeLabel={strings.sheet.close}
          busy={completeWork.isPending}
          onCancel={() => setDialog(null)}
          onConfirm={() => completeHere()}
        />
      )}

      {dialog === 'proof' && (
        <WorkProofSheet
          kit={kit}
          mandatory={requiresProof(workProof)}
          onCancel={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            finishAfterProof();
          }}
        />
      )}

      {askingMovedOn && (
        <ConfirmSheet
          title={strings.movedOn.title}
          statement={strings.movedOn.statement(kit.jobOrderNo, STAGE_LABEL[kit.stage])}
          confirmLabel={strings.movedOn.view}
          cancelLabel={strings.movedOn.no}
          closeLabel={strings.sheet.close}
          onConfirm={() => setMovedOnAnswer('view')}
          onCancel={leaveCase}
        />
      )}
      {dialog === 'pause' && (
        <PauseConfirm
          jobOrderNo={kit.jobOrderNo}
          elapsedSeconds={elapsed}
          busy={pauseWork.isPending}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            pauseWork.mutate({ kitId: kit.id }, { onSettled: () => setDialog(null) })
          }
        />
      )}

      {dialog === 'flag' && (
        <StagePickerDialog
          title={`${strings.flag.title} ${kit.jobOrderNo}`}
          lead={strings.flag.lead}
          confirmLabel={strings.flag.confirm}
          destinations={flagDestinationsFor(kit)}
          statement={(to, person) =>
            strings.confirm.flag(kit.jobOrderNo, to === null ? null : STAGE_LABEL[to], person)
          }
          requireNote
          busy={flagKit.isPending}
          kitId={kit.id}
          onCancel={() => setDialog(null)}
          onConfirm={(toStage: Stage, note: string, assignToUserId: string | null) => {
            flagKit.mutate(
              { kitId: kit.id, toStage, note, assignToUserId },
              {
                onSuccess: (result) => {
                  setDialog(null);
                  haptic('warning');
                  offerUndo(
                    `${kit.jobOrderNo} ${strings.job.flagged} ${strings.job.sentTo} ${STAGE_LABEL[toStage]}`,
                    result.kit.undo,
                    'neutral',
                  );
                  leaveCase();
                },
                onError: (error: Error) =>
                  reportCommandFailure(error, () => {
                    setDialog(null);
                    leaveCase();
                  }),
              },
            );
          }}
        />
      )}

      {/*
        Mark done, with the destination already decided and only the person to
        choose. Section 5.2 of the request of 20 September 2026.
      */}
      {dialog === 'person' && suggested !== null && (
        <StagePickerDialog
          title={strings.routeChoice.title}
          lead={strings.routeChoice.personLead}
          confirmLabel={strings.routeChoice.confirm}
          destinations={[suggested]}
          statement={(to, person) =>
            strings.confirm.done(kit.jobOrderNo, to === null ? null : STAGE_LABEL[to], person)
          }
          initial={suggested}
          peopleOnly
          requireNote={false}
          optionalNote
          busy={completeWork.isPending}
          kitId={kit.id}
          onCancel={() => setDialog(null)}
          onConfirm={(toStage: Stage, note: string, assignToUserId: string | null) => {
            completeWork.mutate(
              { kit, nextStage: null, assignToUserId, note },
              {
                onSuccess: (result) => {
                  setDialog(null);
                  haptic('success');
                  offerUndo(
                    `${kit.jobOrderNo} ${strings.job.completed} · ${strings.job.sentTo} ${STAGE_LABEL[toStage]}`,
                    result.kit.undo,
                  );
                  leaveCase();
                },
                // The destination was never a choice, so a refusal leaves nothing
                // to change here: the sheet closes and the toast gives the reason.
                onError: (error: Error) => {
                  setDialog(null);
                  reportCommandFailure(error, leaveCase);
                },
              },
            );
          }}
        />
      )}

      {dialog === 'route' && (
        <StagePickerDialog
          title={strings.routeChoice.title}
          lead={offRoute ? strings.routeChoice.lead : strings.routeChoice.manualLead}
          confirmLabel={strings.routeChoice.confirm}
          destinations={routeDestinations}
          statement={(to, person) =>
            strings.confirm.done(kit.jobOrderNo, to === null ? null : STAGE_LABEL[to], person)
          }
          initial={suggested}
          requireNote={false}
          optionalNote
          busy={completeWork.isPending}
          kitId={kit.id}
          onCancel={() => setDialog(null)}
          onConfirm={(toStage: Stage, note: string, assignToUserId: string | null) => {
            completeWork.mutate(
              { kit, nextStage: toStage, assignToUserId, note },
              {
                onSuccess: (result) => {
                  setDialog(null);
                  haptic('success');
                  offerUndo(
                    `${kit.jobOrderNo} ${strings.job.completed} · ${strings.job.sentTo} ${STAGE_LABEL[toStage]}`,
                    result.kit.undo,
                  );
                  leaveCase();
                },
                onError: (error: Error) =>
                  reportCommandFailure(error, () => {
                    setDialog(null);
                    leaveCase();
                  }),
              },
            );
          }}
        />
      )}
    </div>
  );
}

function OptionRow({
  label,
  options,
  selected,
}: {
  label: string;
  options: string[];
  selected: string;
}) {
  return (
    <div className={styles.field}>
      <div className={styles.l}>{label}</div>
      <div className={styles.opts}>
        {options.map((option) => (
          <span key={option} className={`${styles.opt} ${option === selected ? styles.on : ''}`}>
            {option}
          </span>
        ))}
      </div>
    </div>
  );
}


